'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

import {
  Avatar,
  Badge as MokoBadge,
  Skeleton,
  toast,
} from '@mokoboko/components';

import {
  splitClockTime,
  formatPrice,
  getAbbreviatedName,
  mergeClassNames,
  formatDurationBetween,
} from '@/lib/utils';
import {
  findActiveEntryForSlot,
  fullCardBadgeOf,
  getWaitlistAction,
  getWaitlistGate,
} from '@/helpers/waitlist';
import { bookingReceiptPathFor } from '@/helpers/booked-classes';
import {
  classCardFlagsOf,
  isOneTimeClass,
  spotsLeftOf,
  type SpotsTone,
} from '@/helpers/class';
import { useAuth } from '@/contexts/auth';
import useTenant, { useTimeFormat } from '@/state/tenant';
import useMyWaitlistEntries from '@/state/waitlist';
import useBookedClasses from '@/state/booked-classes';
import { JoinWaitlistModal } from '@/components/features/waitlist/join-waitlist-modal';
import { AppearanceSettings } from '@/state/appearance-settings';
import { Class } from '@/state/class-groups';
import { CartData } from '@/lib/types';
import { trackMetaConversion } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { goToCheckout } from '@/helpers/checkout-navigation';
import { openSitePage } from '@/helpers/embed-navigation';
import { classDatePath } from '@/helpers/offering';
import { getMinSpotsPerBooking } from '@/helpers/min-spots';
import type { ScheduleInstructor } from '@/helpers/instructor';
import { cardImageOf } from '@/lib/entity-image';
import { useDetailHref, useIsEmbed } from '@/contexts/embed';
import { CARD_TITLE_LINK_CLASS } from '@/lib/prose';
import { SubstituteBadge } from './substitute-badge';
import { FocalPointImage } from './focal-point-image';
import { ClassActionButton } from './class-action-button';

// The badge sits outside the truncating button so a long name shortens
// instead of squeezing the badge; the shared wrapper keeps the badge on the
// name's line instead of wrapping below it.
const InstructorChip = ({
  instructor,
  instructorName,
  isSubstitute,
  onClick,
}: {
  instructor: ScheduleInstructor;
  instructorName: string;
  isSubstitute?: boolean;
  onClick: (adminUserId: string) => void;
}) => (
  <span className="flex min-w-0 max-w-full items-center gap-2">
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(instructor.admin_user_id);
      }}
      className={mergeClassNames(
        'group/instructor relative flex min-w-0 items-center gap-2 rounded text-left [&_*]:cursor-pointer',
        'before:absolute before:-inset-x-1 before:-inset-y-2 before:content-[""]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2'
      )}
    >
      <Avatar
        src={instructor.profile_picture ?? undefined}
        name={`${instructor.first_name} ${instructor.last_name ?? ''}`.trim()}
        size="xs"
      />
      <span className="truncate text-gray-700 underline-offset-4 transition-colors group-hover/instructor:text-red-400 group-hover/instructor:underline">
        {instructorName}
      </span>
    </button>
    {isSubstitute && <SubstituteBadge />}
  </span>
);

const Dot = () => <span className="text-gray-400">·</span>;

// error-* stays stock under studio branding; the red-* stops do not.
const SPOTS_TONE_CLASS: Record<SpotsTone, string> = {
  open: 'text-gray-700',
  few: 'font-medium text-orange-700',
  last: 'font-medium text-error-600',
};

type ClassCardProps = {
  classData: Class;
  classSettings: AppearanceSettings['class'];
  onInstructorClick: (adminUserId: string) => void;
};

export const ClassCard = ({
  classData,
  classSettings,
  onInstructorClick,
}: ClassCardProps) => {
  const router = useRouter();
  const detailHref = useDetailHref('class', classData.class_id, classData.slug);
  const timeFormat = useTimeFormat();
  const [isJoinWaitlistOpen, setIsJoinWaitlistOpen] = useState(false);
  const { data: tenant } = useTenant();
  const { user } = useAuth();
  const isEmbed = useIsEmbed();

  const instructorSchedule = classData?.instructor_schedules?.[0];
  const instructor = instructorSchedule?.admin_user;
  const instructorName = instructor
    ? getAbbreviatedName(instructor.first_name, instructor.last_name)
    : '';
  const slot = instructorSchedule?.weekly_availability?.[0]?.slots?.[0];
  const isOneTime = isOneTimeClass(classData);
  const isFullyBooked = !!slot?.is_fully_booked;
  const { data: myWaitlistEntries } = useMyWaitlistEntries({
    enabled: isFullyBooked,
  });
  const { data: bookedClasses } = useBookedClasses({
    email: user?.email ?? '',
    enabled: isFullyBooked,
    staleTime: 30_000,
  });

  const gateReason = slot
    ? getWaitlistGate({
        startTime: slot.start_time,
        isFullyBooked: slot.is_fully_booked,
        waitlistEnabled: slot.waitlist_enabled,
        waitlistCount: slot.waitlist_count,
        config: tenant?.waitlist_config,
      })
    : 'NOT_FULL';
  const myWaitlistEntry = slot
    ? findActiveEntryForSlot(myWaitlistEntries, slot)
    : undefined;
  // The server would refuse her a place on a class time she already holds.
  const bookedPath =
    isFullyBooked && slot ? bookingReceiptPathFor(bookedClasses, slot) : null;
  const waitlistAction =
    isFullyBooked && !bookedPath
      ? getWaitlistAction(gateReason === null, myWaitlistEntry)
      : null;
  // A full card stays clickable while the class detail page is still useful:
  // the customer can join, is already in line, or holds a booking there.
  const isCardClickable = !isFullyBooked || !!waitlistAction || !!bookedPath;
  const fullBadge = fullCardBadgeOf({
    isBooked: !!bookedPath,
    hasEntry: !!myWaitlistEntry,
    gateReason,
  });
  const flags = classCardFlagsOf(classSettings);
  const spotsLeft =
    flags.showSpots && slot?.total_spots != null && slot.total_spots !== 1
      ? spotsLeftOf(slot.participants_limit, slot.total_spots)
      : null;
  const spotsLeftText = spotsLeft && (
    <span className={SPOTS_TONE_CLASS[spotsLeft.tone]}>{spotsLeft.label}</span>
  );
  const startTime = slot ? splitClockTime(slot.start_time, timeFormat) : null;
  const instructorChip =
    flags.showInstructor && instructor ? (
      <InstructorChip
        instructor={instructor}
        instructorName={instructorName}
        isSubstitute={instructorSchedule?.is_substituted}
        onClick={onInstructorClick}
      />
    ) : null;
  const showInstructor = instructorChip !== null;

  const { url: cardImageUrl, focalX, focalY } = cardImageOf(classData);
  const hasCardImage = !!cardImageUrl;

  const handleCardClick = () => {
    router.push(detailHref);
  };

  const handleViewBookingClick = () => {
    if (bookedPath) router.push(bookedPath);
  };

  const handleWaitlistClick = () => {
    if (waitlistAction === 'view' && myWaitlistEntry) {
      router.push(`/waitlist/${myWaitlistEntry.waitlist_entry_id}`);
      return;
    }
    // Joining needs a first-party session, which a framed embed never has,
    // so the embed hands off to the studio's own class page.
    if (isEmbed && slot) {
      if (!openSitePage(classDatePath(classData.slug, slot.start_time))) {
        toast.info('Allow pop-ups to join the waitlist.');
      }
      return;
    }
    setIsJoinWaitlistOpen(true);
  };

  const handleButtonClick = () => {
    if (!slot) {
      router.push(detailHref);
      return;
    }

    const minSpots = getMinSpotsPerBooking(classData);
    const cartData: CartData<'class'> = {
      items: [
        {
          class_id: classData.class_id,
          slug: classData.slug,
          name: classData.name,
          price: classData.price ?? 0,
          quantity: minSpots,
          instructor_name: instructorName,
          slot_id: slot?.slot_id ?? null,
          override_slot_id: slot?.override_slot_id ?? null,
          start_time: slot?.start_time.toString(),
          end_time: slot?.end_time.toString(),
          available_spots: slot?.participants_limit,
          min_spots_per_booking: minSpots,
          timezone: instructorSchedule?.timezone,
          instructor_schedule_id: instructorSchedule?.instructor_schedule_id,
          currency: classData.currency,
          image: cardImageUrl,
          kind: classData.kind,
        },
      ],
      totalAmount: (classData.price ?? 0) * minSpots,
      checkoutType: 'class',
    };

    if (!goToCheckout(router, cartData)) {
      toast.info('Allow pop-ups to continue to checkout.');
      return;
    }

    trackMetaConversion({
      event: 'AddToCart',
      custom_data: {
        content_name: classData.name,
        content_type: 'class',
        content_ids: [classData.slug],
        value: classData.price ?? 0,
        currency: classData.currency,
      },
    });
    track({
      event: AnalyticsEvent.PRODUCT_ADDED_TO_CART,
      properties: {
        product_type: 'class',
        product_name: classData.name,
        product_id: classData.class_id,
        price: classData.price ?? null,
        currency: classData.currency ?? null,
        quantity: minSpots,
      },
    });
  };

  return (
    <div className="flex w-full flex-row gap-4">
      <div className="flex w-[96px] flex-shrink-0 flex-col gap-3 rounded-xl bg-gray-100 py-5 pl-4 pr-3 md:w-[100px] md:px-4">
        <div className="flex flex-col gap-1.5">
          {slot && startTime && (
            <p className="whitespace-nowrap text-base font-semibold text-gray-900">
              {startTime.clock}
              {startTime.period && (
                <span className="ml-0.5 text-xs font-medium text-gray-600">
                  {' '}
                  {startTime.period}
                </span>
              )}
            </p>
          )}
          {/* A one-time class says it runs once where a weekly class gives its
              length: above a daily list the day is already the heading. */}
          {isOneTime ? (
            <p className="whitespace-nowrap text-sm leading-5 text-gray-700">
              One-time
            </p>
          ) : (
            slot &&
            flags.showDuration && (
              <p className="text-sm text-gray-700">
                {formatDurationBetween(
                  slot.start_time as unknown as string,
                  slot.end_time as unknown as string
                )}
              </p>
            )
          )}
        </div>
        {flags.showPrice && (
          <div className="md:hidden">
            <MokoBadge
              label={formatPrice(classData?.price, classData?.currency)}
              color="gray"
              size="md"
              className="bg-gray-200"
            />
          </div>
        )}
      </div>
      <div
        className={mergeClassNames(
          'group flex w-full overflow-hidden rounded-xl border border-gray-200 shadow-xs transition-shadow duration-200 max-md:flex-col',
          isCardClickable
            ? 'cursor-pointer md:hover:shadow-lg'
            : 'cursor-default'
        )}
        onClick={isCardClickable ? handleCardClick : undefined}
      >
        {fullBadge && (
          <MokoBadge
            label={fullBadge.label}
            color={fullBadge.color}
            size="md"
            className="h-7 w-full justify-center rounded-b-none rounded-t-[11px] border-0 px-4 py-1 md:hidden"
          />
        )}
        <div
          className={mergeClassNames(
            'flex w-full flex-col gap-5 p-4 md:gap-[18px] md:p-5',
            flags.showDescription ? 'md:items-start' : '',
            !hasCardImage || !flags.showImage ? 'md:gap-8' : ''
          )}
        >
          <div className="flex flex-col gap-1.5 md:hidden">
            <Link
              href={detailHref}
              className={mergeClassNames(
                CARD_TITLE_LINK_CLASS,
                isCardClickable && 'group-hover:text-red-500'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {classData?.name}
            </Link>
            <div className="flex w-full items-center gap-1.5 text-sm text-gray-700">
              {instructorChip}

              {spotsLeftText && (
                <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                  {showInstructor && <Dot />}
                  {spotsLeftText}
                </span>
              )}
            </div>
            {flags.showDescription && classData?.short_description && (
              <p className="mt-1.5 text-sm text-gray-700">
                {classData.short_description}
              </p>
            )}
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="flex items-center gap-2.5">
                <Link
                  href={detailHref}
                  className={mergeClassNames(
                    CARD_TITLE_LINK_CLASS,
                    isCardClickable && 'group-hover:text-red-500'
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {classData?.name}
                </Link>
                {fullBadge && (
                  <MokoBadge
                    label={fullBadge.label}
                    color={fullBadge.color}
                    size="sm"
                  />
                )}
              </div>
              <div className="flex w-full flex-wrap items-center gap-1.5 text-sm text-gray-700">
                {instructorChip}

                {spotsLeftText && (
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {showInstructor && <Dot />}
                    {spotsLeftText}
                  </span>
                )}

                {flags.showPrice && (
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {(showInstructor || spotsLeftText) && <Dot />}
                    <span className="text-gray-700">
                      {formatPrice(classData?.price, classData?.currency)}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0">
              <ClassActionButton
                isFullyBooked={isFullyBooked}
                isBooked={!!bookedPath}
                waitlistAction={waitlistAction}
                onViewBookingClick={handleViewBookingClick}
                onWaitlistClick={handleWaitlistClick}
                onBookClick={handleButtonClick}
              />
            </div>
          </div>

          {flags.showDescription && classData?.short_description && (
            <p className="hidden text-sm text-gray-700 md:block">
              {classData.short_description}
            </p>
          )}

          {hasCardImage && flags.showImage && (
            <div
              className={mergeClassNames(
                'relative h-[200px] w-auto shrink-0 overflow-hidden rounded-xl md:hidden'
              )}
            >
              <FocalPointImage
                src={cardImageUrl!}
                focalPointX={focalX}
                focalPointY={focalY}
                className="absolute"
              />
            </div>
          )}

          <div className="w-full md:hidden">
            <ClassActionButton
              isFullyBooked={isFullyBooked}
              isBooked={!!bookedPath}
              waitlistAction={waitlistAction}
              onViewBookingClick={handleViewBookingClick}
              onWaitlistClick={handleWaitlistClick}
              onBookClick={handleButtonClick}
              fullWidth
            />
          </div>
        </div>
        {hasCardImage && flags.showImage && (
          <div
            className={mergeClassNames(
              'max-md:pb-1/2 relative hidden h-[200px] w-auto shrink-0 rounded-l-[10px] bg-gray-100 max-md:w-full md:block md:h-[100px] md:w-[100px]',
              flags.showDescription
                ? 'h-full w-[162px] md:h-full md:w-[162px]'
                : ''
            )}
          >
            <FocalPointImage
              src={cardImageUrl!}
              focalPointX={focalX}
              focalPointY={focalY}
              className="absolute"
            />
          </div>
        )}
      </div>

      {instructorSchedule && slot && isJoinWaitlistOpen && (
        <JoinWaitlistModal
          onClose={() => setIsJoinWaitlistOpen(false)}
          classData={classData}
          slot={{
            slot_id: slot.slot_id,
            override_slot_id: slot.override_slot_id,
            instructor_schedule_id: instructorSchedule.instructor_schedule_id,
            start_time: new Date(slot.start_time).toISOString(),
            timezone: instructorSchedule.timezone,
          }}
          instructorName={instructorName}
        />
      )}
    </div>
  );
};

// Read off the same switches as the card, so the list keeps its height when
// the cards land. Whether a class has an image or a description is not known
// until then, so both are assumed. Beside the time rail the column is narrow,
// so the description takes two lines.
export const ClassCardSkeleton = ({
  classSettings,
}: {
  classSettings: AppearanceSettings['class'] | undefined;
}) => {
  const { showImage, showPrice, showDuration, showDescription } =
    classCardFlagsOf(classSettings);

  return (
    <div className="flex w-full flex-row gap-4">
      <div className="flex w-[96px] flex-shrink-0 flex-col gap-3 rounded-xl bg-gray-100 py-5 pl-4 pr-3 md:w-[100px] md:px-4">
        <div className="flex flex-col gap-1.5">
          <Skeleton text="body" width={44} />
          {showDuration && <Skeleton text="body-sm" width={46} />}
        </div>
        {showPrice && (
          <div className="md:hidden">
            <Skeleton height={24} width={60} />
          </div>
        )}
      </div>
      <div className="flex w-full overflow-hidden rounded-xl border border-gray-200 shadow-xs max-md:flex-col">
        <div
          className={mergeClassNames(
            'flex w-full flex-col gap-5 p-4 md:gap-[18px] md:p-5',
            showDescription && 'md:items-start',
            !showImage && 'md:gap-8'
          )}
        >
          <div className="flex w-full flex-col gap-1.5 md:hidden">
            <Skeleton text="body-lg" width="100%" className="max-w-44" />
            <Skeleton text="body" width="100%" className="max-w-52" />
            {showDescription && (
              <div className="mt-1.5 flex flex-col">
                <Skeleton text="body-sm" />
                <Skeleton text="body-sm" className="w-3/4" />
              </div>
            )}
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton text="body-lg" width={176} />
              <Skeleton text="body" width={280} />
            </div>
            <Skeleton isRounded height={40} width={112} className="shrink-0" />
          </div>

          {showDescription && (
            <div className="hidden w-full md:block">
              <Skeleton text="body-sm" className="w-3/4" />
            </div>
          )}

          {showImage && (
            <Skeleton
              isRounded
              height={200}
              width="100%"
              className="md:hidden"
            />
          )}

          <Skeleton isRounded height={40} width="100%" className="md:hidden" />
        </div>

        {showImage &&
          (showDescription ? (
            <Skeleton
              height="100%"
              width={162}
              className="hidden shrink-0 self-stretch !rounded-none md:block"
            />
          ) : (
            <Skeleton
              height={100}
              width={100}
              className="hidden shrink-0 !rounded-none md:block"
            />
          ))}
      </div>
    </div>
  );
};
